import wrapFetch, { FetchLibrary, RequestInitRetryParams } from 'fetch-retry';
import partialRight from 'lodash/partialRight';

import { useNetworkStore } from '../stores/networkStore';

export const FETCH_RETRIES = 3;
export const FETCH_RETRY_DELAY = 1000;
const FETCH_TIMEOUT_DEFAULT = 5000;

type FetchTimeout = {
  id: NodeJS.Timeout;
  abortSignal: AbortSignal;
};

export type RequestInitWithRetry = RequestInit &
  RequestInitRetryParams<FetchLibrary>;

type RetryOnFunction = (
  attempt: number,
  error: Error | null,
  response?: Response | null
) => boolean;

const retryOn = (
  attempt: number,
  error: Error | null,
  response?: Response | null,
  statusCodes?: number[]
): boolean => {
  const hasStatusOnList =
    response?.status && statusCodes?.includes(response.status);

  if (!error && !hasStatusOnList) {
    return false;
  } // resolve when there is no error
  if (attempt > FETCH_RETRIES) {
    return false;
  } // resolve error after all retries

  if (error?.name === 'AbortError') {
    return false;
  } // do not retry on abort
  return true;
};

export const retryOnStatusCode = (statusCode: number[]): RetryOnFunction =>
  partialRight(retryOn, statusCode);

let _fetchRetry = wrapFetch(fetch, {
  retries: FETCH_RETRIES,
  retryDelay: FETCH_RETRY_DELAY,
  retryOn,
});

export const setFetchClient = (): void => {
  _fetchRetry = wrapFetch(fetch, {
    retries: FETCH_RETRIES,
    retryDelay: FETCH_RETRY_DELAY,
    retryOn,
  });
};

export class OfflineError extends Error {
  constructor() {
    super();

    this.name = 'OfflineError';
    this.message = 'No network was detected on fetch failure';
  }
}

/**
 * Class for our own fetch error.
 * This is used to identify errors that we do care (from within our app),
 * in the meantime to ignore random fetch errors from third party scripts.
 */
export class OwnFetchError extends Error {
  constructor(_error: Error) {
    super(_error.message, { cause: _error.cause });

    this.name = _error.name;
  }
}

export async function fetchRetry(
  input: RequestInfo,
  init?: RequestInitWithRetry
): Promise<Response> {
  return _fetchRetry(input, init).catch((err) => {
    if (typeof window !== 'undefined' && window.navigator.onLine === false) {
      useNetworkStore.getState().setStatus(false);
      throw new OfflineError(); // offline errors are handled in useErrorHandling
    } else if (err instanceof Error) {
      // By `own` we mean that the error happens with requests fired from our "own" app.
      // This applies to *all* errors caught here, because only our code can call this function.
      // But since we don't treat offline as a real error, so that's filtered out.
      throw new OwnFetchError(err);
    } else {
      // According to MDN, there can only be two kinds of exceptions for the fetch API,
      // and they are all with the `Error` type.
      // Reference: https://developer.mozilla.org/en-US/docs/Web/API/fetch#exceptions
      // So this condition should never happen, we added it here just to be on the safe side.
      throw err;
    }
  });
}

/**
 * try to fetch data and handle errors
 */
export const tryFetchRetry = async <T>(
  input: RequestInfo,
  init?: RequestInitWithRetry,
  returnRawResponse = false,
  timeToExpire = 0
): Promise<T | { success: false; error: string; errorObject?: Error }> => {
  try {
    let timeout;
    if (timeToExpire > 0) {
      timeout = setupRequestTimeout(timeToExpire);
      init = {
        ...(init ?? {}),
        signal: timeout.abortSignal,
      };
    }
    const response = await fetchRetry(input, init);
    clearTimeout(timeout?.id);
    return returnRawResponse ? response : await response.json();
  } catch (error) {
    if (error instanceof OfflineError) {
      throw error;
    } // offline errors are handled in useErrorHandling
    const isError = error instanceof OwnFetchError;
    const errorMessage = isError ? error.message : String(error);
    return {
      success: false,
      error: errorMessage,
      errorObject: isError ? error : undefined,
    };
  }
};

const setupRequestTimeout = (
  timeToExpire = FETCH_TIMEOUT_DEFAULT
): FetchTimeout => {
  const abortController = new AbortController();
  const id = setTimeout(() => {
    abortController.abort();
  }, timeToExpire);

  return { id, abortSignal: abortController.signal };
};
